<template>
  <section class="travel-planner-hero">
    <div class="travel-planner-hero__bg-image"></div>
    <div class="travel-planner-hero__container">
      <p
        v-if="label"
        class="travel-planner-hero__label"
      >
        {{ label }}
      </p>
      <div class="travel-planner-hero__container-inner">
        <h1
          v-if="heading"
          class="travel-planner-hero__heading-only"
        >
          {{ heading }}
        </h1>
        <h1
          v-else
          class="travel-planner-hero__heading"
        >
          {{ origin }}
          <span class="travel-planner-hero__icon">
            <svg
              v-if="returnDate"
              class="travel-planner-hero__roundtrip"
            >
              <use :xlink:href="`#${roundTrip}`" />
            </svg>
            <svg
              v-else
              class="travel-planner-hero__arrow"
            >
              <use :xlink:href="`#${arrow}`" />
            </svg>
          </span>
          {{ destination }}
        </h1>
        <p
          v-if="description"
          class="travel-planner-hero__description"
        >
          {{ description }}
        </p>
        <div class="travel-planner-hero__info">
          <div v-if="!description && showInfo">
            <span class="travel-planner-hero__calendar-node">
              <svg class="travel-planner-hero__calendar">
                <use :xlink:href="`#${calendar}`" />
              </svg>
              {{ formattedDate }}
            </span>
            <span class="travel-planner-hero__travlers">
              <svg class="travel-planner-hero__calendar">
                <use :xlink:href="`#${person}`" />
              </svg>
              {{ passengerText }}
            </span>
            <span
              v-if="discountCard"
              class="travel-planner-hero__travlers"
            >
              <svg class="travel-planner-hero__calendar">
                <use :xlink:href="`#${discount}`" />
              </svg>
              {{ discountCard }}
            </span>
          </div>
          <div
            v-if="showChangeButton"
            class="travel-planner-hero__change-button"
          >
            <button
              class="button button--small button--secondary"
              @click="openEditSearch"
            >
              {{ $t('travelPlanner.hero.changeSearch') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <EditSearch
      v-if="isEditSearchOpen"
      @close="closeEditSearch"
    />
  </section>
</template>

<script lang="ts">
  import { defineComponent, computed, ref } from 'vue'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useRoute } from 'vue-router'
  import { formatDateRange } from '@/utils/dateFormatter'
  import arrow from '@/assets/icons/arrow.svg'
  import roundTrip from '@/assets/icons/roundTrip.svg'
  import calendar from '@/assets/icons/calendar.svg'
  import person from '@/assets/icons/person.svg'
  import discount from '@/assets/icons/discount.svg'
  import EditSearch from '@/components/TravelPlanner/EditSearch/EditSearch.vue'
  import { useI18n } from 'vue-i18n'
  import type { DiscountCard } from '@/types/passengerTypes'

  export default defineComponent({
    name: 'TravelPlannerHero',
    components: {
      EditSearch,
    },
    props: {
      label: {
        type: String,
        required: false,
      },
      heading: {
        type: String,
        required: false,
      },
      description: {
        type: String,
        required: false,
      },
      showInfo: {
        type: Boolean,
        default: true,
      },
      showChangeButton: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const route = useRoute()
      const isEditSearchOpen = ref(false)
      const { t } = useI18n()

      const origin = computed(() => journeyStore.OriginStation.name)
      const destination = computed(() => journeyStore.destinationStation.name)
      const departureDate = computed(() => journeyStore.departureDate)
      const returnDate = computed(() => journeyStore.returnDate)

      const formattedDate = computed(() =>
        formatDateRange(departureDate.value, returnDate.value)
      )

      const passengerText = computed(() => {
        const passengers =
          route.query.isRebooking === 'true' &&
          bookingStore.bookingData?.passengers
            ? bookingStore.bookingData.passengers
            : journeyStore.passengers

        if (!passengers?.length) return ''

        const passengerCounts = passengers.reduce((acc, passenger) => {
          const description = passenger.description
          if (description) {
            acc[description] = (acc[description] || 0) + 1
          }
          return acc
        }, {} as Record<string, number>)

        const passengerDescriptions = Object.keys(passengerCounts)
        if (passengerDescriptions.length === 1) {
          const description = passengerDescriptions[0]
          const count = passengerCounts[description]
          return `${count} ${description}`
        } else {
          const totalPassengers = passengers.length
          return `${totalPassengers} ${t('travelPlanner.hero.passengers')}`
        }
      })

      const discountCard = computed(() => {
        const passengers =
          route.query.isRebooking === 'true' &&
          bookingStore.bookingData?.passengers
            ? bookingStore.bookingData.passengers
            : journeyStore.passengers

        if (!passengers?.length) return ''

        const allDiscountCards = passengers.reduce<DiscountCard[]>(
          (cards, passenger) => {
            if (passenger.discount_cards?.length) {
              cards.push(...passenger.discount_cards)
            }
            return cards
          },
          []
        )

        if (!allDiscountCards.length) return ''

        const uniqueDiscountCards = allDiscountCards.filter(
          (card, index, self) =>
            index === self.findIndex((c) => c.code === card.code)
        )
        return uniqueDiscountCards.map((card) => card.name).join(', ')
      })

      const openEditSearch = () => {
        isEditSearchOpen.value = true
        document.body.style.overflow = 'hidden'
      }

      const closeEditSearch = () => {
        isEditSearchOpen.value = false
        document.body.style.overflow = 'auto'
      }

      return {
        origin,
        destination,
        formattedDate,
        passengerText,
        returnDate,
        arrow,
        calendar,
        roundTrip,
        person,
        discount,
        isEditSearchOpen,
        openEditSearch,
        closeEditSearch,
        discountCard,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .travel-planner-hero {
    position: sticky;
    z-index: 30;
    margin-right: -16px;
    margin-left: -16px;
    margin-bottom: 20px;
    top: 68px;
    padding-right: 16px;
    padding-left: 16px;
    color: $clr-primary-800;
    background-color: $clr-white;
    box-shadow: $box-shadow-card;

    @media screen and (max-height: 1000px) {
      &:has(+ section .seat-reservation-page__main) {
        position: relative;
        top: 0;
      }
    }

    &__bg-image {
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 0;
      width: 322px;
      height: 78px;
      background: transparent url(@/assets/icons/landscape.svg) bottom left
        no-repeat;
      opacity: 0.2;
    }

    &__container {
      position: relative;
      z-index: 1;
      padding-top: rem(12);
      padding-bottom: rem(12);
      max-width: $main-width;
      margin: 0 auto;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 12px;

      &-inner {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        gap: 6px;
      }
    }

    &__label {
      @include body-large;
      text-align: left;
      margin: 0;
      color: $clr-gray;
    }

    &__heading {
      @include title-medium;
      color: $clr-primary;
      padding: 0;
      margin: 0;
      display: inline-flex;
      align-items: flex-end;
      flex-wrap: wrap;

      @media screen and (min-width: $breakpoint-sm) {
        margin-bottom: rem(16);
        @include headline-large;
      }
    }

    &__heading-only {
      padding-top: rem(16);
      @include title-medium;
      color: $clr-primary;
      margin: 0 0 rem(5) 0;
      display: flex;
      align-items: center;

      @media screen and (min-width: $breakpoint-xxs) {
        @include headline-large;
      }
    }

    &__description {
      @include body-medium;
      margin: 0;
      padding-bottom: rem(32);
      text-align: left;

      @media screen and (min-width: $breakpoint-xxs) {
        @include body-large;
      }
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      margin: 0 8px;
      padding: 3px;
      color: $clr-white;
      background-color: $clr-secondary;
      border-radius: 50%;

      @media screen and (min-width: $breakpoint-sm) {
        position: relative;
        top: rem(-4);
      }
    }

    &__roundtrip {
      width: 12px;
      height: 12px;
    }

    &__arrow {
      transform: rotate(180deg);
      width: 10px;
      height: 10px;
    }

    &__info {
      @include body-medium;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;

      @media screen and (min-width: $breakpoint-sm) {
        @include body-large;
        gap: 24px;
      }

      span {
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
        gap: 4px;
      }
    }

    &__calendar-node {
      margin-right: 8px;
    }

    &__calendar {
      width: 16px;
      height: 16px;
      color: $clr-secondary;
    }

    &__travlers {
      border-left: 1px solid $clr-primary-100;
      border-right: none;
      margin: 0 0 8px 0;
      padding: 0 8px;
    }

    &__change-button {
      display: flex;
      justify-content: flex-end;
    }
  }
</style>
